import React from "react";
import Card from "components/card";
import Button from "components/button/Button";
import CommonModal from "components/modals/Modal";
import { createPortal } from "react-dom";
import { useDisclosure } from "@chakra-ui/hooks";
import { useNavigate } from "react-router-dom";
import { numericFormatter } from "react-number-format";
import { FaBoxes, FaTrash } from "react-icons/fa";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@chakra-ui/modal";
import { ICategory, Image } from "views/admin/goods/types/goods.interface";
import { DiscountType } from "constants/discount";

const ProductCard = (props: {
  id: string;
  thumbnail: string;
  images: Image[];
  title: string;
  category?: ICategory;
  category_detail?: ICategory;
  price: {
    original_price: number;
    price_after_discount: number;
    discount: number;
    type: DiscountType;
    end_at: string;
  };
  sku: string;
  description: string;
  extra?: string;
  slug: string;
  stock?: number;
  onDelete?: (slug: string) => void;
  onClickEdit?: () => void;
}) => {
  const navigate = useNavigate();
  const {
    title,
    category,
    category_detail,
    price,
    thumbnail,
    images,
    sku,
    description,
    extra,
    slug,
    stock,
    onDelete,
    onClickEdit,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalAction, setModalAction] = React.useState<
    "show" | "confirmation"
  >();
  const [imageTarget, setImageTarget] = React.useState<string>("");

  return (
    <>
      <Card
        extra={`flex flex-col w-full h-full !p-4 3xl:p-![18px] bg-white ${extra}`}
      >
        <div className="h-full w-full">
          <div className="relative mb-1 w-full overflow-hidden">
            {thumbnail ? (
              <img
                src={
                  thumbnail
                    ? !thumbnail.includes("https")
                      ? `${process.env.REACT_APP_PUBLIC_URL}/${
                          imageTarget !== "" ? imageTarget : thumbnail
                        }`
                      : thumbnail
                    : "https://placehold.co/600x400?text=No+Thumbnail"
                }
                className="mb-3 h-56 w-full rounded-xl object-cover object-center"
                alt=""
              />
            ) : (
              <div className="flex h-56 w-full items-center justify-center rounded-xl bg-brand-50 bg-opacity-25">
                <FaBoxes className="h-12 w-12 text-brand-500" />
              </div>
            )}
          </div>

          <div className="mb-3 flex items-center justify-between px-1 md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col xl:items-start 3xl:flex-row 3xl:justify-between">
            <div className="flex w-full">
              <div className="mb-2 flex flex-col overflow-hidden">
                <p className="truncate text-lg font-bold text-navy-700 dark:text-white">
                  {" "}
                  {title}
                </p>
                <div className="flex flex-col">
                  {stock ? (
                    <p className="mt-1 text-base font-semibold text-gray-700">
                      Stok : {stock}
                    </p>
                  ) : (
                    <></>
                  )}
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    Kategori : {category?.name ?? category_detail?.name ?? "-"}
                  </p>
                  <p className="text-sm font-medium text-gray-600">
                    SKU : {sku || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col 2xl:items-start 3xl:flex-row 3xl:items-center 3xl:justify-between">
            <div className="mb-3 flex flex-col items-start">
              {price.discount > 0 ? (
                <>
                  <p className="text-sm font-bold text-red-700 line-through dark:text-white">
                    {numericFormatter(price?.original_price?.toString(), {
                      decimalSeparator: ",",
                      thousandSeparator: ".",
                      prefix: "Rp",
                    })}
                  </p>
                  <h3 className="text-lg font-bold text-brand-500 dark:text-white">
                    {numericFormatter(price?.price_after_discount?.toString(), {
                      decimalSeparator: ",",
                      thousandSeparator: ".",
                      prefix: "Rp",
                    })}
                  </h3>
                </>
              ) : (
                <h3 className="mt-5 text-lg font-bold text-brand-500 dark:text-white">
                  {numericFormatter(price?.original_price?.toString(), {
                    decimalSeparator: ",",
                    thousandSeparator: ".",
                    prefix: "Rp",
                  })}
                </h3>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              {onDelete && (
                <Button
                  size="fit"
                  children={
                    <div className="flex h-5 items-center">
                      <FaTrash className="text-xs text-white" />
                    </div>
                  }
                  variant="danger"
                  onClick={() => {
                    setModalAction("confirmation");
                    onOpen();
                  }}
                />
              )}
              <Button
                size="fit"
                children="Edit"
                variant="gray"
                onClick={() => {
                  if (onClickEdit) {
                    onClickEdit();
                  } else {
                    navigate(`/admin/goods/edit/${slug}`);
                  }
                }}
              />
              <Button
                size="fit"
                children="Detail"
                variant="primary"
                onClick={() => {
                  setModalAction("show");
                  onOpen();
                }}
              />
            </div>
          </div>
        </div>
      </Card>
      {createPortal(
        <CommonModal
          size="2xl"
          children={
            <div>
              <ModalHeader>
                {modalAction === "show" ? "Detail Barang" : "Konfirmasi"}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {modalAction === "show" ? (
                  <div className={`flex h-full w-full flex-col bg-white`}>
                    <div className="h-full w-full">
                      <div className="relative w-full overflow-hidden">
                        {thumbnail ? (
                          <img
                            src={
                              thumbnail
                                ? !imageTarget.includes("https")
                                  ? `${process.env.REACT_APP_PUBLIC_URL}/${
                                      imageTarget !== ""
                                        ? imageTarget
                                        : thumbnail
                                    }`
                                  : imageTarget
                                : "https://placehold.co/600x400?text=No+Thumbnail"
                            }
                            className="mb-3 h-full w-full rounded-xl object-cover object-center transition-all duration-300 3xl:h-full 3xl:w-full"
                            alt=""
                          />
                        ) : (
                          <div className="flex h-56 w-full items-center justify-center rounded-xl bg-brand-50 bg-opacity-25">
                            <FaBoxes className="h-12 w-12 text-brand-500" />
                          </div>
                        )}
                      </div>
                      <div className="mb-4 mt-2 flex items-center justify-start gap-2">
                        {images?.map((image, idx) => {
                          return (
                            <div
                              key={idx}
                              className="cursor-pointer"
                              onClick={() => setImageTarget(image?.path)}
                            >
                              <img
                                src={`${
                                  !image.path.includes("https")
                                    ? `${process.env.REACT_APP_PUBLIC_URL}/${image?.path}`
                                    : image?.path
                                }`}
                                alt={title}
                                className="mb-3 h-20 max-h-20 w-20 rounded-xl object-cover object-center 3xl:h-20 3xl:w-20"
                              />
                            </div>
                          );
                        })}
                      </div>

                      <div className="mb-3 flex items-center justify-between px-1 md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col xl:items-start 3xl:flex-row 3xl:justify-between">
                        <div className="mb-2">
                          <p className="text-lg font-bold text-navy-700 dark:text-white">
                            {" "}
                            {title}{" "}
                          </p>
                          <div className="flex flex-col gap-1">
                            {stock ? (
                              <p className="mt-1 text-base font-semibold text-gray-700">
                                Stok : {stock}
                              </p>
                            ) : (
                              <></>
                            )}
                            <p className="mt-1 text-sm font-medium text-gray-600">
                              Kategori : {category?.name || "-"}
                            </p>
                            <p className="text-sm font-medium text-gray-600">
                              SKU : {sku || "-"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col 2xl:items-start 3xl:flex-row 3xl:items-center 3xl:justify-between">
                        <div className="flex">
                          {/* <p className="text-sm font-bold text-red-700 line-through dark:text-white">
                            {numericFormatter(
                              price?.original_price?.toString(),
                              {
                                decimalSeparator: ",",
                                thousandSeparator: ".",
                                prefix: "Rp",
                              }
                            )}
                          </p>
                          <h3 className="text-lg font-bold text-brand-500 dark:text-white">
                            {numericFormatter(
                              price?.price_after_discount?.toString(),
                              {
                                decimalSeparator: ",",
                                thousandSeparator: ".",
                                prefix: "Rp",
                              }
                            )}
                          </h3> */}
                        </div>
                      </div>
                      <p className="mt-2 text-base text-gray-700 dark:text-white">
                        {description}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-base text-gray-700 dark:text-white">
                      Apakah anda yakin ingin menghapus barang <br />
                      <span className="text-brand-500">{title}</span>
                    </p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                {modalAction === "show" ? (
                  <Button
                    variant="gray"
                    children="Tutup"
                    size="fit"
                    onClick={onClose}
                  />
                ) : (
                  <div className="flex gap-3">
                    <Button
                      size="fit"
                      children="Batal"
                      variant="gray"
                      onClick={onClose}
                    />
                    <Button
                      size="fit"
                      children={<span className="text-white">Ya, hapus</span>}
                      variant="danger"
                      onClick={() => {
                        onClose();
                        onDelete && onDelete(slug);
                      }}
                    />
                  </div>
                )}
              </ModalFooter>
            </div>
          }
          isOpen={isOpen}
          onClose={onClose}
        />,
        document.body
      )}
    </>
  );
};

export default ProductCard;
